import { itemAssets } from "../data/itemAssets";
import itemCatalog from "../data/itemCatalog.json";
import { DEFAULT_ITEM_STATS, itemStats } from "../data/itemStats";
import { getUniqueEffects } from "../data/uniqueEffects";
import itemStatsDraft from "../data/itemStatsDraft.json";
import verifiedItemMap from "../data/verifiedItemMap.json";
import { findItemStatsDraft } from "./itemStatsDraftMerge";

const GRADE_ORDER = {
  rare: 0,
  epic: 1,
  legend: 2,
  mythic: 3,
  common: 4,
  uncommon: 5,
  unknown: 6,
};
const FORCE_ITEM_MAP = {
  203408: "Cube Watch",
  203503: "Auto-arms",
  204510: "엘디안 부츠",
  203511: "Chain of Thorns",
  204507: "Galaxy Steps",
  201502: "빛의 증표",
  202410: "아마조네스 아머",
  203505: "가시지네 견갑",
  202415: "Battle Suit",
  203409: "아이기스",
  202423: "교복",
  201407: "기사단장의 투구",
  201518: "쿼드아이",
};

const itemCatalogByCode = Object.fromEntries(
  itemCatalog
    .map((item) => [String(item.itemCode ?? item.item_code ?? item.code ?? "").trim(), item])
    .filter(([code]) => code)
);
const verifiedEntries = Object.entries(verifiedItemMap || {}).map(([key, value]) => ({
  ...value,
  key,
}));
const draftEntries = Object.entries(itemStatsDraft || {}).map(([key, value]) => ({
  ...value,
  key,
}));
const itemAssetEntries = Object.values(itemAssets);
const draftCodeLookup = buildDraftCodeLookup(draftEntries);
const draftLookup = buildDraftLookup(draftEntries);
const simulatorItems = buildSimulatorItems();
const simulatorLookup = buildSimulatorLookup(simulatorItems);
const fallbackLookup = buildFallbackLookup([...itemAssetEntries, ...verifiedEntries]);

function normalizeGrade(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  const aliases = {
    common: "common",
    normal: "common",
    basic: "common",
    "일반": "common",
    uncommon: "uncommon",
    advanced: "uncommon",
    "고급": "uncommon",
    rare: "rare",
    "희귀": "rare",
    epic: "epic",
    heroic: "epic",
    hero: "epic",
    "영웅": "epic",
    legend: "legend",
    legendary: "legend",
    "전설": "legend",
    mythic: "mythic",
    transcendent: "mythic",
    transcendence: "mythic",
    "초월": "mythic",
    "신화": "mythic",
  };

  return aliases[normalized] || "";
}

function getGradeOrder(grade) {
  return GRADE_ORDER[normalizeGrade(grade) || "unknown"] ?? GRADE_ORDER.unknown;
}

function compareSimulatorItems(a, b) {
  const gradeCompare = getGradeOrder(a.grade) - getGradeOrder(b.grade);
  if (gradeCompare) return gradeCompare;

  const numberCompare = (a.fileNumber ?? Number.MAX_SAFE_INTEGER) - (b.fileNumber ?? Number.MAX_SAFE_INTEGER);
  if (numberCompare) return numberCompare;

  return String(a.displayName || a.name || "").localeCompare(String(b.displayName || b.name || ""), "ko");
}

function normalizeItemStatsEntry(entry) {
  if (!entry) {
    return {
      itemSkill: null,
      stats: {},
    };
  }

  if (entry.stats || entry.itemSkill) {
    return {
      itemSkill: entry.itemSkill || null,
      stats: entry.stats || {},
    };
  }

  return {
    itemSkill: null,
    stats: entry,
  };
}

function buildSimulatorItems() {
  return Object.values(itemAssets)
    .map((item) => {
      const verified = verifiedItemMap?.[`${item.slotType}/${item.fileName}`] || null;
      const itemCodeStats = normalizeItemStatsEntry(itemStats[item.itemCode]);
      const codeStats = normalizeItemStatsEntry(itemStats[item.code]);
      const draft = findItemStatsDraft(item);
      const draftStats = draft?.stats || {};
      const structuredEffects = getUniqueEffects(draft?.uniqueEffectIds);
      const itemSkill = structuredEffects[0] || draft?.itemSkill || item.itemSkill || codeStats.itemSkill || itemCodeStats.itemSkill || null;
      const itemSkills = structuredEffects.length > 0 ? structuredEffects : draft?.itemSkills || item.itemSkills || [];
      const matchedName = verified?.matchedName || verified?.displayName || item.matchedName || item.displayName || "";

      return {
        ...item,
        englishName: item.englishName || verified?.englishName || item.name || "",
        matchedName,
        displayName: matchedName || item.displayName || item.name,
        grade: normalizeGrade(item.grade),
        itemSkill,
        itemSkills,
        rawItemSkill: draft?.rawItemSkill || item.rawItemSkill || [],
        rawStats: draft?.rawStats || item.rawStats || [],
        stats: {
          ...DEFAULT_ITEM_STATS,
          ...itemCodeStats.stats,
          ...codeStats.stats,
          ...draftStats,
        },
        uniqueEffects: itemSkills.length > 0 ? itemSkills : itemSkill ? [itemSkill] : [],
      };
    })
    .sort(compareSimulatorItems);
}

function normalizeLookupText(value) {
  return String(value || "")
    .replace(/^.*[\\/]/, "")
    .replace(/\.png$/i, "")
    .replace(/\.[a-f0-9]{6,}$/i, "")
    .replace(/^\d+\.\s*/, "")
    .replace(/[_'"`']/g, " ")
    .replace(/[^0-9a-zA-Z가-힣]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function addLookupEntry(lookup, candidate, item) {
  const key = normalizeLookupText(candidate);
  if (key && !lookup[key]) {
    lookup[key] = item;
  }
}

function buildSimulatorLookup(items) {
  return items.reduce((lookup, item) => {
    getSimulatorCandidates(item).forEach((candidate) => addLookupEntry(lookup, candidate, item));
    return lookup;
  }, {});
}

function buildFallbackLookup(items) {
  return items.reduce((lookup, item) => {
    getFallbackCandidates(item).forEach((candidate) => addLookupEntry(lookup, candidate, item));
    return lookup;
  }, {});
}

function buildDraftLookup(items) {
  return items.reduce((lookup, item) => {
    getDraftCandidates(item).forEach((candidate) => addLookupEntry(lookup, candidate, item));
    return lookup;
  }, {});
}

function buildDraftCodeLookup(items) {
  return items.reduce((lookup, item) => {
    const code = String(item?.code ?? "").trim();

    if (!code) {
      return lookup;
    }

    if (lookup[code]) {
      console.warn("[itemStatsDraft] duplicated code", code, lookup[code], item);
      return lookup;
    }

    lookup[code] = item;
    return lookup;
  }, {});
}

function getDraftCandidates(item) {
  if (!item) return [];

  return [
    item.key,
    item.displayName,
    item.name,
    item.matchedName,
    item.koreanName,
    item.englishName,
    item.english_name,
    item.itemName,
    item.item_name,
    item.itemCode,
    item.item_code,
    item.code,
    item.fileName,
  ].filter(Boolean);
}

function getSimulatorCandidates(item) {
  if (!item) return [];

  return [
    item.englishName,
    item.english_name,
    item.name,
    item.matchedName,
    item.koreanName,
    item.displayName,
    item.itemName,
    item.item_name,
    item.fileName,
    item.key,
    item.code,
    item.image,
    item.itemImage,
    item.item_image,
    item.imageUrl,
    item.image_url,
  ].filter(Boolean);
}

function getFallbackCandidates(item) {
  if (!item) return [];

  return [
    item.englishName,
    item.english_name,
    item.name,
    item.matchedName,
    item.koreanName,
    item.displayName,
    item.itemName,
    item.item_name,
    item.fileName,
    item.key,
    item.code,
    item.image,
    item.itemImage,
    item.item_image,
    item.imageUrl,
    item.image_url,
  ].filter(Boolean);
}

function getItemCode(input) {
  if (input === null || input === undefined) return "";
  if (typeof input === "string" || typeof input === "number") return String(input).trim();

  return String(
    input.itemCode
    ?? input.item_code
    ?? input.code
    ?? input.id
    ?? input.itemId
    ?? input.item_id
    ?? ""
  ).trim();
}

function isUnknownName(value, code = "") {
  const text = String(value || "").trim();

  return !text
    || /^Unknown\(/i.test(text)
    || (code && text === String(code))
    || (code && text === `Item ${code}`);
}

function firstKnownName(values, code) {
  return values.find((value) => !isUnknownName(value, code)) || "";
}

function firstPresent(values) {
  return values.find((value) => (
    value !== null
    && value !== undefined
    && String(value).trim() !== ""
  ));
}

function encodeAssetPath(path) {
  return path ? encodeURI(path) : "";
}

function findByCandidates(candidates, lookup) {
  for (const candidate of candidates) {
    const key = normalizeLookupText(candidate);
    if (key && lookup[key]) {
      return lookup[key];
    }
  }

  return null;
}

function getInputCandidates(input, catalogEntry, fallbackEntry) {
  return [
    input.englishName,
    input.english_name,
    catalogEntry?.englishName,
    catalogEntry?.english_name,
    fallbackEntry?.englishName,
    fallbackEntry?.english_name,
    input.matchedName,
    input.koreanName,
    input.itemName,
    input.item_name,
    input.name,
    catalogEntry?.matchedName,
    catalogEntry?.koreanName,
    catalogEntry?.itemName,
    catalogEntry?.item_name,
    catalogEntry?.name,
    fallbackEntry?.matchedName,
    fallbackEntry?.koreanName,
    fallbackEntry?.displayName,
    fallbackEntry?.itemName,
    fallbackEntry?.item_name,
    fallbackEntry?.name,
    input.fileName,
    input.key,
    fallbackEntry?.fileName,
    fallbackEntry?.key,
    input.image,
    input.itemImage,
    input.item_image,
    input.imageUrl,
    input.image_url,
    fallbackEntry?.image,
    fallbackEntry?.itemImage,
    fallbackEntry?.item_image,
    fallbackEntry?.imageUrl,
    fallbackEntry?.image_url,
  ].filter(Boolean);
}

function getFallbackEntry(input, catalogEntry) {
  return findByCandidates(getInputCandidates(input, catalogEntry, null), fallbackLookup) || {};
}

function getDraftEntry(input, catalogEntry, fallbackEntry, simulatorItem, forcedName = "") {
  return findByCandidates([
    forcedName,
    ...getInputCandidates(input, catalogEntry, fallbackEntry),
    ...getSimulatorCandidates(simulatorItem),
  ], draftLookup) || null;
}

function getSimulatorEntryFromDraft(draftEntry) {
  if (!draftEntry) {
    return null;
  }

  return findByCandidates(getDraftCandidates(draftEntry), simulatorLookup);
}

function getSimulatorEntry(input, catalogEntry, fallbackEntry) {
  const forcedName = FORCE_ITEM_MAP[getItemCode(input)];
  const forcedItem = forcedName
    ? findByCandidates([forcedName], simulatorLookup)
    : null;

  if (forcedItem) {
    return forcedItem;
  }

  return findByCandidates(getInputCandidates(input, catalogEntry, fallbackEntry), simulatorLookup);
}

function getEffectListFromDraft(draftEntry, simulatorItem = {}) {
  const structuredEffects = getUniqueEffects(draftEntry?.uniqueEffectIds);
  if (structuredEffects.length > 0) return structuredEffects;
  if (Array.isArray(draftEntry?.uniqueEffects)) return draftEntry.uniqueEffects;
  if (Array.isArray(draftEntry?.itemSkills)) return draftEntry.itemSkills;
  if (draftEntry?.itemSkill) return [draftEntry.itemSkill];
  if (Array.isArray(simulatorItem.uniqueEffects)) return simulatorItem.uniqueEffects;
  if (Array.isArray(simulatorItem.itemSkills)) return simulatorItem.itemSkills;
  return simulatorItem.itemSkill ? [simulatorItem.itemSkill] : [];
}

function toSimulatorInfo(simulatorItem, inputCode, draftEntry = null, source = "simulator") {
  const code = inputCode || simulatorItem.itemCode || simulatorItem.item_code || simulatorItem.code || "";
  const name = firstKnownName([
    simulatorItem.matchedName,
    simulatorItem.koreanName,
    simulatorItem.displayName,
    simulatorItem.itemName,
    simulatorItem.item_name,
    simulatorItem.name,
  ], code);
  const image = encodeAssetPath(simulatorItem.image || simulatorItem.itemImage || simulatorItem.item_image || "");

  return {
    code,
    itemCode: code,
    item_code: code,
    englishName: simulatorItem.englishName || simulatorItem.english_name || simulatorItem.name || "",
    english_name: simulatorItem.englishName || simulatorItem.english_name || simulatorItem.name || "",
    name,
    itemName: name,
    item_name: name,
    matchedName: simulatorItem.matchedName || "",
    image,
    itemImage: image,
    item_image: image,
    grade: normalizeGrade(draftEntry?.grade || simulatorItem.grade),
    itemGrade: normalizeGrade(draftEntry?.grade || simulatorItem.grade),
    item_grade: normalizeGrade(draftEntry?.grade || simulatorItem.grade),
    rarity: normalizeGrade(draftEntry?.grade || simulatorItem.grade),
    slotType: simulatorItem.slotType || "",
    stats: draftEntry?.stats || simulatorItem.stats || {},
    itemSkill: draftEntry?.itemSkill || simulatorItem.itemSkill || null,
    itemSkills: draftEntry?.itemSkills || simulatorItem.itemSkills || [],
    rawItemSkill: draftEntry?.rawItemSkill || simulatorItem.rawItemSkill || [],
    rawStats: draftEntry?.rawStats || simulatorItem.rawStats || [],
    uniqueEffects: getEffectListFromDraft(draftEntry, simulatorItem),
    isUnknown: false,
    raw: null,
    source,
  };
}

function toDraftInfo(draftEntry, inputCode, fallbackEntry = {}, input = {}) {
  const code = inputCode || draftEntry.code || draftEntry.itemCode || draftEntry.item_code || "";
  const name = firstKnownName([
    draftEntry.matchedName,
    draftEntry.koreanName,
    draftEntry.displayName,
    draftEntry.itemName,
    draftEntry.item_name,
    draftEntry.name,
  ], code) || (code ? `Unknown(${code})` : "");
  const image = encodeAssetPath(firstPresent([
    draftEntry.image,
    draftEntry.itemImage,
    draftEntry.item_image,
    draftEntry.imageUrl,
    draftEntry.image_url,
    fallbackEntry.image,
    fallbackEntry.itemImage,
    fallbackEntry.item_image,
    fallbackEntry.imageUrl,
    fallbackEntry.image_url,
    input.image,
    input.itemImage,
    input.item_image,
    input.imageUrl,
    input.image_url,
  ]) || "");
  const grade = normalizeGrade(draftEntry.grade || draftEntry.itemGrade || draftEntry.item_grade || "");

  return {
    code,
    itemCode: code,
    item_code: code,
    englishName: draftEntry.englishName || draftEntry.english_name || "",
    english_name: draftEntry.englishName || draftEntry.english_name || "",
    name,
    itemName: name,
    item_name: name,
    matchedName: draftEntry.matchedName || draftEntry.displayName || "",
    image,
    itemImage: image,
    item_image: image,
    grade,
    itemGrade: grade,
    item_grade: grade,
    rarity: grade,
    slotType: draftEntry.slotType || draftEntry.slot_type || fallbackEntry.slotType || "",
    stats: draftEntry.stats || {},
    itemSkill: draftEntry.itemSkill || null,
    itemSkills: draftEntry.itemSkills || [],
    rawItemSkill: draftEntry.rawItemSkill || [],
    rawStats: draftEntry.rawStats || [],
    uniqueEffects: getEffectListFromDraft(draftEntry, {}),
    isUnknown: false,
    raw: input,
    source: "itemStatsDraft",
  };
}

function toFallbackInfo(input, catalogEntry, fallbackEntry, inputCode, fallbackName = "") {
  const code = inputCode || fallbackEntry.itemCode || fallbackEntry.item_code || fallbackEntry.code || "";
  const name = firstKnownName([
    input.matchedName,
    input.koreanName,
    input.itemName,
    input.item_name,
    input.name,
    catalogEntry?.matchedName,
    catalogEntry?.koreanName,
    catalogEntry?.itemName,
    catalogEntry?.item_name,
    catalogEntry?.name,
    fallbackEntry.matchedName,
    fallbackEntry.koreanName,
    fallbackEntry.displayName,
    fallbackEntry.itemName,
    fallbackEntry.item_name,
    fallbackEntry.name,
    fallbackName,
  ], code) || (code ? `Unknown(${code})` : "");
  const image = encodeAssetPath(firstPresent([
    input.itemImage,
    input.item_image,
    input.image,
    input.imageUrl,
    input.image_url,
    fallbackEntry.image,
    fallbackEntry.itemImage,
    fallbackEntry.item_image,
    fallbackEntry.imageUrl,
    fallbackEntry.image_url,
    catalogEntry?.itemImage,
    catalogEntry?.item_image,
    catalogEntry?.image,
    catalogEntry?.imageUrl,
    catalogEntry?.image_url,
  ]) || "");
  const isUnknown = !name || /^Unknown\(/i.test(name);

  return {
    code,
    itemCode: code,
    item_code: code,
    englishName: fallbackEntry.englishName || fallbackEntry.english_name || fallbackEntry.name || "",
    english_name: fallbackEntry.englishName || fallbackEntry.english_name || fallbackEntry.name || "",
    name,
    itemName: name,
    item_name: name,
    matchedName: fallbackEntry.matchedName || input.matchedName || "",
    image,
    itemImage: image,
    item_image: image,
    grade: normalizeGrade(fallbackEntry.grade || fallbackEntry.itemGrade || fallbackEntry.item_grade || ""),
    itemGrade: normalizeGrade(fallbackEntry.grade || fallbackEntry.itemGrade || fallbackEntry.item_grade || ""),
    item_grade: normalizeGrade(fallbackEntry.grade || fallbackEntry.itemGrade || fallbackEntry.item_grade || ""),
    rarity: normalizeGrade(fallbackEntry.grade || fallbackEntry.itemGrade || fallbackEntry.item_grade || ""),
    slotType: fallbackEntry.slotType || catalogEntry?.item_type || "",
    stats: {},
    itemSkill: null,
    itemSkills: [],
    rawItemSkill: [],
    rawStats: [],
    uniqueEffects: [],
    isUnknown,
    raw: input,
    source: isUnknown ? "unknown" : "fallback",
  };
}

function reportUnresolvedDetails(itemInfo, input, catalogEntry, fallbackEntry) {
  if (process.env.NODE_ENV !== "development" || itemInfo.source === "simulator") {
    return;
  }

  console.warn("[itemResolver] unresolved item details", {
    itemCode: itemInfo.code,
    name: itemInfo.name,
    englishName: itemInfo.englishName,
    matchedName: itemInfo.matchedName,
    image: itemInfo.image,
    reason: "simulator item not found",
    sourceArea: input.sourceArea || "",
    catalogEntry,
    fallbackEntry,
  });
}

export function getItemInfo(input, fallbackName = "") {
  const objectInput = typeof input === "object" && input ? input : {};
  const inputCode = getItemCode(input);
  const catalogEntry = inputCode && itemCatalogByCode[inputCode] ? itemCatalogByCode[inputCode] : objectInput.meta || null;
  const fallbackEntry = getFallbackEntry(objectInput, catalogEntry);
  const codeDraftEntry = inputCode ? draftCodeLookup[String(inputCode)] || null : null;
  const forcedName = FORCE_ITEM_MAP[inputCode] || "";
  const simulatorItem = codeDraftEntry
    ? getSimulatorEntryFromDraft(codeDraftEntry)
    : getSimulatorEntry(objectInput, catalogEntry, fallbackEntry);
  const draftEntry = codeDraftEntry || getDraftEntry(objectInput, catalogEntry, fallbackEntry, simulatorItem, forcedName);
  const itemInfo = simulatorItem
    ? toSimulatorInfo(simulatorItem, inputCode, draftEntry, codeDraftEntry ? "itemStatsDraft" : forcedName ? "force-map" : draftEntry ? "itemStatsDraft" : "simulator")
    : draftEntry
      ? toDraftInfo(draftEntry, inputCode, fallbackEntry, objectInput)
      : toFallbackInfo(objectInput, catalogEntry, fallbackEntry, inputCode, fallbackName);

  if (!simulatorItem && draftEntry && itemInfo.source !== "itemStatsDraft") {
    itemInfo.stats = draftEntry.stats || itemInfo.stats;
    itemInfo.grade = normalizeGrade(draftEntry.grade || itemInfo.grade);
    itemInfo.itemGrade = itemInfo.grade;
    itemInfo.item_grade = itemInfo.grade;
    itemInfo.uniqueEffects = getEffectListFromDraft(draftEntry, {});
    itemInfo.itemSkill = draftEntry.itemSkill || null;
    itemInfo.itemSkills = draftEntry.itemSkills || [];
    itemInfo.rawItemSkill = draftEntry.rawItemSkill || [];
    itemInfo.rawStats = draftEntry.rawStats || [];
    itemInfo.source = "itemStatsDraft";
    itemInfo.isUnknown = false;
  }

  reportUnresolvedDetails(itemInfo, objectInput, catalogEntry, fallbackEntry);

  return {
    ...objectInput,
    ...itemInfo,
  };
}

export function normalizeItemList(value, sourceArea = "") {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => getItemInfo(
        typeof item === "object" && item ? { ...item, sourceArea } : { code: item, sourceArea },
      ))
      .filter((item) => item.code || item.name);
  }

  if (typeof value === "object") {
    return Object.values(value)
      .map((item) => getItemInfo(
        typeof item === "object" && item ? { ...item, sourceArea } : { code: item, sourceArea },
      ))
      .filter((item) => item.code || item.name);
  }

  return [];
}

export function getSimulatorItems() {
  return simulatorItems;
}

export function getSimulatorItemsBySlot(slotType) {
  return simulatorItems.filter((item) => (
    item.slotType === slotType
    && item.simulatorSelectable !== false
  ));
}
